/**
 * @file schemas.ts
 * @brief Zod schemas + TS types for the Super Admin → Feedback feature.
 *
 * @details
 * Mirrors the response shapes from `BACKEND/src/features/feedback/routes.js`:
 *   - GET /api/feedback      → { items: session_feedback[], page, pageSize, total, hasMore }
 *   - GET /api/feedback/app  → { items: app_feedback[],     page, pageSize, total, hasMore }
 *   - either with `?group=user` → { groups: FeedbackUserGroup[] } (01-10-2026)
 *
 * `.passthrough()` keeps parsing resilient to additive backend columns.
 *
 * @references
 * - BACKEND/migrations/20260705_create_feedback_tables.sql
 *
 * @author DES
 * @date 05-07-2026
 */

import { z } from "zod";

/**
 * ⚠️ `.catch` means an unknown surface is DISPLAYED as Conversation rather
 * than rejected — a new module's feedback lands silently in the wrong bucket
 * until this list is widened. 'medical' added 19-09-2026.
 */
export const FeedbackSessionTypeSchema = z
  .enum(["meetingintel", "conversation", "realtime", "medical"])
  .catch("conversation");
export type FeedbackSessionType = z.infer<typeof FeedbackSessionTypeSchema>;

// ----------------------------------------------------------------------
// Session feedback row
// ----------------------------------------------------------------------
export const SessionFeedbackSchema = z
  .object({
    id: z.string(),
    session_id: z.string(),
    user_id: z.string().nullable().optional(),
    session_type: FeedbackSessionTypeSchema,
    user_email: z.string().nullable().optional(),
    user_first_name: z.string().nullable().optional(),
    user_last_name: z.string().nullable().optional(),
    overall_rating: z.number(),
    accuracy_rating: z.number().nullable().optional(),
    speed_rating: z.number().nullable().optional(),
    audio_rating: z.number().nullable().optional(),
    resolved: z.boolean().nullable().optional(),
    tags: z.array(z.string()).default([]),
    comment: z.string().nullable().optional(),
    app_version: z.string().nullable().optional(),
    platform: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string().nullable().optional(),
  })
  .passthrough();
export type SessionFeedback = z.infer<typeof SessionFeedbackSchema>;

// ----------------------------------------------------------------------
// App feedback row
// ----------------------------------------------------------------------
export const AppFeedbackSchema = z
  .object({
    id: z.string(),
    user_id: z.string().nullable().optional(),
    user_email: z.string().nullable().optional(),
    user_first_name: z.string().nullable().optional(),
    user_last_name: z.string().nullable().optional(),
    overall_rating: z.number(),
    would_recommend: z.number().nullable().optional(),
    comment: z.string().nullable().optional(),
    routed_to_store: z.boolean().default(false),
    external_platform: z.string().nullable().optional(),
    app_version: z.string().nullable().optional(),
    platform: z.string().nullable().optional(),
    created_at: z.string(),
  })
  .passthrough();
export type AppFeedback = z.infer<typeof AppFeedbackSchema>;

// ----------------------------------------------------------------------
// List responses
// ----------------------------------------------------------------------
const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z
    .object({
      success: z.boolean().optional(),
      items: z.array(item),
      page: z.number(),
      pageSize: z.number(),
      total: z.number(),
      hasMore: z.boolean(),
    })
    .passthrough();

export const SessionFeedbackListResponseSchema = paginated(SessionFeedbackSchema);
export type SessionFeedbackListResponse = z.infer<
  typeof SessionFeedbackListResponseSchema
>;

export const AppFeedbackListResponseSchema = paginated(AppFeedbackSchema);
export type AppFeedbackListResponse = z.infer<
  typeof AppFeedbackListResponseSchema
>;

/**
 * One user in the grouped list (`?group=user`, DES 01-10-2026): every user
 * with feedback under the current filters, with their count.
 */
export const FeedbackUserGroupSchema = z.object({
  user_id: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  count: z.number(),
});
export type FeedbackUserGroup = z.infer<typeof FeedbackUserGroupSchema>;

// ----------------------------------------------------------------------
// List params
// ----------------------------------------------------------------------
export type SessionFeedbackListParams = {
  page?: number;
  pageSize?: number;
  type?: FeedbackSessionType;
  minRating?: number;
  from?: string; // ISO / YYYY-MM-DD (inclusive)
  to?: string; // ISO / YYYY-MM-DD (inclusive)
  search?: string;
  /** Narrow to one user's group: a user id, or `none` (no user). */
  user?: string;
};

export type AppFeedbackListParams = {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
  search?: string;
  /** Narrow to one user's group: a user id, or `none` (no user). */
  user?: string;
};
